import { Loader } from "@/components/Loader";
import { Dialog, FormDatePicker, FormSelect } from "@/components";
import { ArrowRight } from "lucide-react";
import { SwapDialogProps } from "./types";
import { FC } from "react";
import { FormProvider } from "react-hook-form";
import { SECOND_USER_ID, END_DATE, START_DATE } from "./consts";
import { DialogFooter, DialogClose, Button } from "@/components/ui";
import useSwapDialog from "./useSwapDialog";
import moment from "moment";

const SwapDialog: FC<SwapDialogProps> = ({
  swapConfirmDialog,
  setSwapConfirmDialog,
  swapBOMDetails,
}) => {
  const {
    form,
    requiredSwapBomFields,
    onCloseDialog,
    onSwapSubmit,
    isTransferPending,
    isCommunityUsersLoading,
    getUniqueUserList,
    showSwapDialog,
    setShowSwapDialog,
    onSwapConfirm,
  } = useSwapDialog({
    swapBOMDetails,
    setSwapConfirmDialog,
  });

  const { errors } = form.formState;
  return (
    <>
      <Dialog
        open={swapConfirmDialog as boolean}
        setOpen={setSwapConfirmDialog}
        title={
          <div className="text-2xl font-bold text-primary">
            {"Confirmation"}
            <div className="h-1 w-[30%] bg-primary rounded-full" />
          </div>
        }
        saveText="Yes"
        cancelText="No"
        onSave={onSwapConfirm}
      >
        <p className="text-sm">
          Are you sure you want to transfer the{" "}
          <b className="text-primary">{swapBOMDetails?.positionName || ""}</b>{" "}
          position from{" "}
          <b className="text-primary">
            {swapBOMDetails?.firstName || ""} {swapBOMDetails?.lastName || ""}
          </b>
          ?
        </p>
      </Dialog>

      {showSwapDialog && (
        <Dialog
          open={showSwapDialog}
          setOpen={setShowSwapDialog}
          title={
            <div className="text-2xl font-bold text-primary flex items-center gap-2">
              <ArrowRight className="w-6 h-6" />{" "}
              {swapBOMDetails?.positionName || ""}
            </div>
          }
          hideFooter
          contentClassName="w-[1000px]"
        >
          {isCommunityUsersLoading ? (
            <Loader />
          ) : (
            <div className="flex flex-row justify-between">
              <div className="flex flex-col gap-2">
                <p>From</p>
                <p className="text-lg font-bold text-primary">
                  {swapBOMDetails?.firstName} {swapBOMDetails?.lastName}
                </p>
                <p className="text-sm">Role: {swapBOMDetails?.positionName}</p>
                <p className="text-sm">
                  Start Date:{" "}
                  {swapBOMDetails?.startDate
                    ? moment(swapBOMDetails?.startDate).format("MM/DD/YYYY")
                    : ""}
                </p>
                <p className="text-sm">
                  End Date:{" "}
                  {swapBOMDetails?.endDate
                    ? moment(swapBOMDetails?.endDate).format("MM/DD/YYYY")
                    : ""}
                </p>
              </div>
              <div className="flex flex-col gap-2 w-[60%] border-s-1 border-gray-200 ps-6 ms-6">
                <p>To</p>
                <FormProvider {...form}>
                  <form onSubmit={form.handleSubmit(onSwapSubmit)}>
                    <div className="grid grid-cols-1 gap-[30px]">
                      <FormSelect
                        name={SECOND_USER_ID}
                        control={form.control}
                        label="User"
                        placeholder="Please select"
                        error={errors[SECOND_USER_ID]?.message}
                        isRequired={requiredSwapBomFields.includes(
                          SECOND_USER_ID
                        )}
                        options={getUniqueUserList()} //TODO: new API for this
                      />
                      <FormDatePicker
                        name={START_DATE}
                        control={form.control}
                        label="Start Date"
                        placeholder="Please select"
                        error={errors[START_DATE]?.message}
                        isRequired={true}
                        maxDate={new Date()}
                        onClear={() => form.setValue(END_DATE, "")}
                        disabled
                        onChange={(date: any) => {
                          if (
                            moment(date).isAfter(form.getValues()[END_DATE])
                          ) {
                            form.setValue(END_DATE, "");
                          }
                        }}
                      />
                      <FormDatePicker
                        name={END_DATE}
                        control={form.control}
                        label="End Date"
                        placeholder="Please select"
                        error={errors[END_DATE]?.message}
                        isRequired={requiredSwapBomFields.includes(END_DATE)}
                        minDate={new Date(form.watch(START_DATE))}
                        disabled={!form.watch(START_DATE)}
                      />
                    </div>
                    <DialogFooter className="mt-5">
                      <DialogClose>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={isTransferPending}
                          onClick={onCloseDialog}
                        >
                          Cancel
                        </Button>
                      </DialogClose>
                      <Button type="submit" disabled={isTransferPending}>
                        {isTransferPending && <Loader variant="secondary" />}{" "}
                        Transfer
                      </Button>
                    </DialogFooter>
                  </form>
                </FormProvider>
              </div>
            </div>
          )}
        </Dialog>
      )}
    </>
  );
};

export default SwapDialog;
